import { Divider, Pill, Stack, ToggleButton, ToggleButtonGroup } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ECOSYSTEM_CLASSIFICATION,
  EcosystemClassificationType,
} from '@/api/rest/resources/types/ecosystemClassification';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { DonutChart } from '@/components/Charts/DonutChart';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { NCCardSectionHeader } from '@/components/NCCard/NCCard';
import {
  getIucnGetLevelThreeEFGLegendValues,
  getIucnGetLevelTwoBiomeLegendValues,
} from '@/components/widgets/o/Map/utils/getLegends';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';
import { useGetEcosystemClassificationFacts } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/ecosystemClassification/components/ecosystemClassification/hooks/useGetEcosystemClassificationFacts';
import { EcosystemClassificationSelector } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/ecosystemClassification/EcosystemClassificationCard';
import {
  getClassNameValuesFromFacts,
  getGraphDataFromGraphFacts,
} from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/ecosystemClassification/utils';

export const ECOSYSTEM_CLASSIFICATION_CARD_VIEW = {
  DEFAULT: 'default',
  SINGLE_LEVEL: 'single-level',
} as const;

export const EcosystemClassificationGraphTile = ({
  plotGroupId,
  variant = ECOSYSTEM_CLASSIFICATION_CARD_VIEW.DEFAULT,
  category,
  ...props
}: Partial<GraphTileProps> & {
  variant?: (typeof ECOSYSTEM_CLASSIFICATION_CARD_VIEW)[keyof typeof ECOSYSTEM_CLASSIFICATION_CARD_VIEW];
  category?: (typeof ECOSYSTEM_CLASSIFICATION)[keyof typeof ECOSYSTEM_CLASSIFICATION];
}) => {
  return (
    <Stack {...props}>
      {variant === ECOSYSTEM_CLASSIFICATION_CARD_VIEW.DEFAULT && (
        <EcosystemClassificationDefaultView plotGroupId={plotGroupId} />
      )}
      {variant === ECOSYSTEM_CLASSIFICATION_CARD_VIEW.SINGLE_LEVEL && category && (
        <EcosystemClassificationSingleLevelView plotGroupId={plotGroupId} category={category} />
      )}
    </Stack>
  );
};

const EcosystemClassificationDefaultView = ({ plotGroupId }: { plotGroupId?: string | null }) => {
  const [selectedClassification, setSelectedClassification] = useState<EcosystemClassificationType>(
    ECOSYSTEM_CLASSIFICATION.IUCN_GET,
  );

  return (
    <>
      <EcosystemClassificationSelector
        selectedClassification={selectedClassification}
        setSelectedClassification={setSelectedClassification}
      />
      <EcosystemClassificationCardDefaultViewBase category={selectedClassification} plotGroupId={plotGroupId} />
    </>
  );
};

const EcosystemClassificationCardDefaultViewBase = ({
  category,
  plotGroupId,
}: {
  category: EcosystemClassificationType;
  plotGroupId?: string | null;
}) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { firstScalarFacts, secondScalarFacts, graphFacts } = useGetEcosystemClassificationFacts(category, plotGroupId);
  const hasData = firstScalarFacts.length > 0 || secondScalarFacts.length > 0 || graphFacts.length > 0;

  useEffect(() => {
    setIndicatorGraphData({ graphData: hasData });
  }, [hasData, setIndicatorGraphData]);

  if (!hasData) {
    return <NoData />;
  }

  return (
    <Stack data-testid='ecosystem-classification-org-graph-tile'>
      <FirstScalarFactSection firstScalarFacts={firstScalarFacts} category={category} />
      <EcosystemDonutSection facts={secondScalarFacts} level='secondScalarFact' category={category} />
      <EcosystemDonutSection facts={graphFacts} level='graphFact' category={category} />
    </Stack>
  );
};

const EcosystemClassificationSingleLevelView = ({
  plotGroupId,
  category,
}: {
  plotGroupId?: string | null;
  category: (typeof ECOSYSTEM_CLASSIFICATION)[keyof typeof ECOSYSTEM_CLASSIFICATION];
}) => {
  const { t } = useTranslation();
  const [selectedLevel, setSelectedLevel] = useState<Level>('secondScalarFact');
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { secondScalarFacts, graphFacts } = useGetEcosystemClassificationFacts(category, plotGroupId);
  const hasData = secondScalarFacts.length > 0 || graphFacts.length > 0;

  useEffect(() => {
    setIndicatorGraphData({ graphData: hasData });
  }, [hasData, setIndicatorGraphData]);

  if (!hasData) {
    return <NoData />;
  }

  return (
    <Stack data-testid='ecosystem-classification-single-level-view' spacing={4}>
      <Stack className='w-full py-4' center>
        <ToggleButtonGroup value={selectedLevel} onChange={setSelectedLevel} size='small'>
          <ToggleButton value='secondScalarFact'>
            {t(`shared.ncaDetail.ecosystemClassification.${category}.labels.secondScalarFact`)}
          </ToggleButton>
          <ToggleButton value='graphFact'>
            {t(`shared.ncaDetail.ecosystemClassification.${category}.labels.graphFact`)}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {selectedLevel === 'secondScalarFact' && (
        <EcosystemDonutSection facts={secondScalarFacts} level='secondScalarFact' category={category} showChartOnly />
      )}
      {selectedLevel === 'graphFact' && (
        <EcosystemDonutSection facts={graphFacts} level='graphFact' category={category} showChartOnly />
      )}
    </Stack>
  );
};

const FirstScalarFactSection = ({
  firstScalarFacts,
  category,
}: {
  firstScalarFacts: PlotReportFactElement<number | null>[] | undefined;
  category: EcosystemClassificationType;
}) => {
  const { t } = useTranslation();
  const levelOneValues = getClassNameValuesFromFacts({ facts: firstScalarFacts });

  return (
    <Stack spacing={2} className='w-full items-start justify-start py-6 lg:py-6'>
      <NCCardSectionHeader
        title={t(`shared.ncaDetail.ecosystemClassification.${category}.labels.firstScalarFact`)}
        popover={{
          title: t(`shared.ncaDetail.ecosystemClassification.${category}.labels.firstScalarFact`),
          body: t(`shared.ncaDetail.ecosystemClassification.${category}.labels.firstScalarFactTooltip`),
        }}
      />
      <Stack direction='row' spacing={2} className='flex-wrap'>
        {levelOneValues.map((value) => (
          <Pill key={value} size='small' className='bg-info-light text-info'>
            {value}
          </Pill>
        ))}
      </Stack>
    </Stack>
  );
};

type Level = 'secondScalarFact' | 'graphFact';

const EcosystemDonutSection = ({
  facts,
  level,
  category,
  showChartOnly,
}: {
  facts: PlotReportFactElement<number | null>[] | undefined;
  level: Level;
  category: EcosystemClassificationType;
  showChartOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const label = t(`shared.ncaDetail.ecosystemClassification.${category}.labels.${level}`);
  const tooltipLabel = t(`shared.ncaDetail.ecosystemClassification.${category}.labels.${level}Tooltip`);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tAny = t as any;
  const colors = [...getIucnGetLevelThreeEFGLegendValues(), ...getIucnGetLevelTwoBiomeLegendValues()].map((color) => ({
    ...color,
    text: tAny(color.text),
  }));
  const chartData = getGraphDataFromGraphFacts(facts, colors);

  if (chartData.length === 0) {
    return null;
  }

  if (showChartOnly) {
    return <DonutChart data={chartData} />;
  }

  return (
    <>
      <Divider />
      <Stack center>
        <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
          <ChartHeading
            heading={label}
            className='w-full px-6 sm:px-0'
            popover={{
              title: label,
              body: tooltipLabel,
            }}
          />
          <DonutChart data={chartData} />
        </Stack>
      </Stack>
    </>
  );
};
